'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useMediaDeviceSelect } from '@livekit/components-react';
import { Check, ChevronDown, Mic } from 'lucide-react';
import { cn } from './lib/utils';
const LABEL_FALLBACK = 'Microphone (grant access to see name)';
function displayLabel(device, index) {
    if (device.label)
        return device.label;
    if (device.deviceId === 'default')
        return 'Default microphone';
    return `Microphone ${index + 1}`;
}
export function MicPicker() {
    const [open, setOpen] = useState(false);
    const rootRef = useRef(null);
    const { devices, activeDeviceId, setActiveMediaDevice } = useMediaDeviceSelect({
        kind: 'audioinput',
        requestPermissions: true,
    });
    const activeDevice = useMemo(() => devices.find(d => d.deviceId === activeDeviceId) ??
        devices.find(d => d.deviceId === 'default') ??
        devices[0], [devices, activeDeviceId]);
    useEffect(() => {
        if (!open)
            return;
        const onClick = (event) => {
            if (!rootRef.current)
                return;
            if (!rootRef.current.contains(event.target))
                setOpen(false);
        };
        const onKey = (event) => {
            if (event.key === 'Escape')
                setOpen(false);
        };
        window.addEventListener('mousedown', onClick);
        window.addEventListener('keydown', onKey);
        return () => {
            window.removeEventListener('mousedown', onClick);
            window.removeEventListener('keydown', onKey);
        };
    }, [open]);
    const onSelect = useCallback(async (deviceId) => {
        setOpen(false);
        try {
            await setActiveMediaDevice(deviceId);
        }
        catch {
            // Selection errors surface in the Room's onError; safe to swallow here.
        }
    }, [setActiveMediaDevice]);
    const buttonLabel = activeDevice && (activeDevice.label || activeDevice.deviceId === 'default')
        ? displayLabel(activeDevice, 0)
        : LABEL_FALLBACK;
    return (_jsxs("div", { ref: rootRef, className: "relative", children: [_jsxs("button", { type: "button", onClick: () => setOpen(prev => !prev), className: cn('inline-flex h-8 items-center gap-2 rounded-md border border-input bg-background text-sm shadow-xs transition-colors', 'px-2 sm:px-3', 'hover:bg-accent hover:text-accent-foreground', 'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50'), "aria-haspopup": "listbox", "aria-expanded": open, "aria-label": buttonLabel, title: buttonLabel, children: [_jsx(Mic, { className: "h-3.5 w-3.5" }), _jsx("span", { className: "hidden max-w-[180px] truncate text-left sm:inline", children: buttonLabel }), _jsx(ChevronDown, { className: cn('h-3.5 w-3.5 transition-transform', open && 'rotate-180') })] }), _jsx(AnimatePresence, { children: open ? (_jsx(motion.ul, { role: "listbox", "aria-label": "Microphone", initial: { opacity: 0, y: -6, scale: 0.98 }, animate: { opacity: 1, y: 0, scale: 1 }, exit: { opacity: 0, y: -6, scale: 0.98 }, transition: { duration: 0.14, ease: [0.2, 0.8, 0.2, 1] }, className: "absolute left-0 z-50 mt-2 w-[280px] overflow-hidden rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-lg sm:left-auto sm:right-0", style: { backgroundColor: 'var(--popover, #ffffff)' }, children: devices.length === 0 ? (_jsx("li", { className: "px-3 py-2 text-xs text-muted-foreground", children: "No input devices detected." })) : (devices.map((device, index) => {
                        const isActive = device.deviceId === activeDeviceId;
                        return (_jsx("li", { children: _jsxs("button", { type: "button", onClick: () => onSelect(device.deviceId), className: cn('flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm transition-colors', 'hover:bg-accent hover:text-accent-foreground', isActive && 'bg-accent/60'), children: [_jsx(Check, { className: cn('h-3.5 w-3.5 flex-shrink-0', isActive ? 'opacity-100' : 'opacity-0') }), _jsx("span", { className: "truncate", children: displayLabel(device, index) })] }) }, device.deviceId || index));
                    })) })) : null })] }));
}
//# sourceMappingURL=MicPicker.js.map